<template>
  <Teleport to="body">
    <Transition name="popup-fade">
      <div
        v-if="modelValue"
        class="popup-overlay"
        :class="variantClass"
        role="dialog"
        aria-modal="true"
        @click.self="emitClose"
      >
        <div class="popup-dialog" @click.stop>
          <!-- 슬라이드 영역 -->
          <div class="popup-swiper-wrap" :class="variantClass">
            <Swiper
              v-if="safePopups.length > 0"
              class="popup-swiper"
              :modules="[Autoplay, Navigation, Pagination]"
              :slides-per-view="1"
              :loop="safePopups.length > 1"
              :autoplay="
                safePopups.length > 1
                  ? { delay: 3000, disableOnInteraction: false }
                  : false
              "
              :navigation="safePopups.length > 1"
              :pagination="{ clickable: true }"
            >
              <SwiperSlide
                v-for="item in safePopups"
                :key="item.id"
                class="popup-slide"
              >
                <button
                  class="popup-image-button"
                  type="button"
                  @click="handleClickImage(item)"
                >
                  <img
                    :src="resolveImage(item)"
                    :alt="item.alt || '공지 팝업'"
                    @error="handleImageError"
                  />
                </button>
              </SwiperSlide>
            </Swiper>
          </div>

          <!-- 하단 버튼 -->
          <div class="popup-actions">
            <button type="button" class="btn btn-today" @click="emitHideToday">
              오늘은 그만 볼래요
            </button>
            <button type="button" class="btn btn-close" @click="emitClose">
              팝업 닫기
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import {
  computed,
  defineProps,
  defineEmits,
  onBeforeUnmount,
  watchEffect,
} from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const props = defineProps({
  modelValue: { type: Boolean, default: false },

  // "pc" | "mobile"
  variant: { type: String, default: "pc" },

  // [{ id, pcUrl, mobileUrl, redirect, order, ... }]
  popups: { type: Array, default: () => [] },

  // 배경 스크롤 잠금
  lockScroll: { type: Boolean, default: true },
});

const emit = defineEmits([
  "update:modelValue",
  "hide-today", // ✅ 전체 일괄 오늘 숨김
  "close", // ✅ 전체 닫기
  "image-click", // ✅ 슬라이드 클릭 (item 전달)
]);

const variantClass = computed(() =>
  props.variant === "mobile" ? "is-mobile" : "is-pc"
);

const safePopups = computed(() =>
  Array.isArray(props.popups) ? props.popups.filter(Boolean) : []
);

function resolveImage(item) {
  if (!item) return "";
  return props.variant === "mobile"
    ? item.mobileUrl || item.pcUrl || ""
    : item.pcUrl || item.mobileUrl || "";
}

function handleClickImage(item) {
  emit("image-click", item);
}

function emitHideToday() {
  emit("hide-today");
  emit("update:modelValue", false);
}

function emitClose() {
  emit("close");
  emit("update:modelValue", false);
}

function handleImageError(e) {
  // 깨진 이미지 대응 (필요 시 placeholder 처리 가능)
  e.target.style.opacity = "0.35";
}

/* -----------------------------
 * 스크롤 잠금
 * ----------------------------- */
watchEffect(() => {
  if (!props.lockScroll) return;

  if (props.modelValue) {
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
  } else {
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
  }
});

onBeforeUnmount(() => {
  document.documentElement.style.overflow = "";
  document.body.style.overflow = "";
});
</script>

<style scoped lang="scss">
.popup-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);

  display: flex;
  padding: 16px;
}

.popup-overlay.is-pc {
  justify-content: center;
  align-items: center;
}

.popup-overlay.is-mobile {
  justify-content: center;
  align-items: flex-end;
}

.popup-dialog {
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.25);
}

/* -----------------------------
 * PC / Mobile 레이아웃 규칙
 * ----------------------------- */
.popup-dialog:has(.popup-swiper-wrap.is-pc) {
  width: 420px;
}

.popup-dialog:has(.popup-swiper-wrap.is-mobile) {
  width: 100%;
  max-width: 500px;
}

.popup-swiper-wrap {
  position: relative;
  background: #f7f7f7;
}

.popup-swiper-wrap.is-pc {
  width: 420px;
  height: 462px;
}

.popup-swiper-wrap.is-mobile {
  width: 100%;
  max-width: 500px;
  height: 360px;
}

.popup-swiper,
.popup-slide {
  width: 100%;
  height: 100%;
}

.popup-image-button {
  width: 100%;
  height: 100%;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
}

.popup-image-button > img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* -----------------------------
 * 버튼 영역
 * ----------------------------- */
.popup-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-top: 1px solid #eaeaea;
  background: #fff;
}

.btn {
  height: 48px;
  border: 0;
  background: #fff;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
}

.btn + .btn {
  border-left: 1px solid #eaeaea;
}

.btn-today {
  color: #444;
}

.btn-close {
  color: #111;
}

/* -----------------------------
 * Swiper UI 튜닝
 * ----------------------------- */
:deep(.swiper-button-prev),
:deep(.swiper-button-next) {
  color: #fff;
  width: 32px;
  height: 32px;
  margin-top: -16px;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.45);
}

:deep(.swiper-button-prev::after),
:deep(.swiper-button-next::after) {
  font-size: 16px;
  font-weight: 700;
}

:deep(.swiper-pagination) {
  bottom: 8px !important;
}

:deep(.swiper-pagination-bullet) {
  background: #fff;
  opacity: 0.55;
}

:deep(.swiper-pagination-bullet-active) {
  opacity: 1;
}

/* -----------------------------
 * 트랜지션
 * ----------------------------- */
.popup-fade-enter-active,
.popup-fade-leave-active {
  transition: opacity 0.18s ease;
}

.popup-fade-enter-from,
.popup-fade-leave-to {
  opacity: 0;
}
</style>
